import { useEffect, useRef, type Dispatch, type SetStateAction } from 'react'
import type { FormState } from '../handoff/page.generated'

/** 이벤트에서만 세션을 변경한다. 취소/재열기/시간 전환/후속 편집·요청 뒤 늦은 응답은 초안에 닿지 않는다. */
export function usePriorityModalSession(setForm: Dispatch<SetStateAction<FormState>>, setOpen: Dispatch<SetStateAction<boolean>>) {
  const generation = useRef(0)
  const identity = useRef(0)
  useEffect(
    () => () => {
      generation.current++
      identity.current++
    },
    [],
  )
  const replace = (form: FormState): void => {
    generation.current++
    setForm(form)
  }
  const close = (): void => {
    identity.current++
    generation.current++
    setOpen(false)
  }
  const capture = () => {
    const modal = identity.current
    const session = ++generation.current
    const isCurrent = (): boolean => session === generation.current
    return {
      isCurrent,
      // Persisted identity belongs to this open modal, even after a later edit.
      // It must never attach to a cancelled/reopened modal.
      applyIdentity: (patch: FormState): void => {
        if (modal === identity.current) setForm((current) => (modal === identity.current ? { ...current, ...patch } : current))
      },
      applyDraft: (patch: FormState): void => {
        if (isCurrent()) setForm((current) => (isCurrent() ? { ...current, ...patch } : current))
      },
    }
  }
  return {
    replace,
    patch: (patch: FormState): void => {
      generation.current++
      setForm((current) => ({ ...current, ...patch }))
    },
    edit: (field: string, value: unknown): void => {
      generation.current++
      setForm((current) => ({ ...current, [field]: value }))
    },
    close,
    capture,
    open: (form: FormState): void => {
      identity.current++
      replace(form)
      setOpen(true)
    },
  }
}
